import type { OutreachKpis } from '@discovr/types';
import { formatRate } from './crmFormat';

export const ACCEPT_SENT_COLOR = 'var(--crm-accept-sent)';
export const ACCEPT_EDITED_COLOR = 'var(--crm-accept-edited)';
const EMPTY_COLOR = 'var(--crm-chart-track)';
const FULL_PERCENT = 100;

export const NO_DRAFTS_SENT = 'No agent drafts sent yet.';

export type AcceptRateKpis = Pick<OutreachKpis, 'agentAcceptRate' | 'draftsSent' | 'draftsSentUnedited' | 'agentAcceptRateByKind'>;

export interface AcceptRateModel {
  /** At least one agent draft was sent, so the rates mean something. */
  measured: boolean;
  /** 0–1; null while no agent draft has been sent. */
  sentAsDrafted: number | null;
  editedBeforeSending: number | null;
  segments: { pct: number; color: string }[];
  /** The donut in one sentence for screen readers. */
  summary: string;
}

/**
 * The rate is null exactly when nothing has been sent, which draws an empty
 * track and dashes rather than a fabricated 0%/100% split.
 */
export function acceptRateModel({ agentAcceptRate, draftsSent, draftsSentUnedited }: AcceptRateKpis): AcceptRateModel {
  if (agentAcceptRate === null || draftsSent <= 0) {
    return {
      measured: false,
      sentAsDrafted: null,
      editedBeforeSending: null,
      segments: [{ pct: FULL_PERCENT, color: EMPTY_COLOR }],
      summary: NO_DRAFTS_SENT,
    };
  }
  const edited = (draftsSent - draftsSentUnedited) / draftsSent;
  return {
    measured: true,
    sentAsDrafted: agentAcceptRate,
    editedBeforeSending: edited,
    segments: [
      { pct: agentAcceptRate * FULL_PERCENT, color: ACCEPT_SENT_COLOR },
      { pct: edited * FULL_PERCENT, color: ACCEPT_EDITED_COLOR },
    ],
    summary: `Sent as drafted ${formatRate(agentAcceptRate)}, edited before sending ${formatRate(edited)}.`,
  };
}

const ACCEPT_KINDS = [
  { label: 'First touches', key: 'initial' },
  { label: 'Follow-ups', key: 'followup' },
  { label: 'Replies', key: 'reply' },
] as const;

export interface AcceptKindRow {
  key: (typeof ACCEPT_KINDS)[number]['key'];
  label: string;
  acceptRate: number | null;
}

export function acceptRateByKind(kpis: Pick<AcceptRateKpis, 'agentAcceptRateByKind'>): AcceptKindRow[] {
  return ACCEPT_KINDS.map(({ key, label }) => ({ key, label, acceptRate: kpis.agentAcceptRateByKind[key] }));
}
